// Machine reason codes the API attaches to a 403's `details` when the portal
// access gate refuses an authenticated user.
//
// The source of truth is `DenyReason` in apps/api/src/lib/portalAccess.ts. The
// web app cannot import from the API workspace, so the union is mirrored here.
// Only the CODES are shared — the user-facing wording for each one lives solely
// in auth/AdminNoAccess.tsx, so the API never renders prose and the two can
// never drift apart.
const DENY_REASONS = ['not_in_group', 'not_provisioned', 'access_check_unavailable'] as const;

export type DenyReason = (typeof DENY_REASONS)[number];

// Recognises ONLY the codes above. Both readers below are built on it, so the
// list of valid codes exists exactly once.
function asDenyReason(value: unknown): DenyReason | null {
  return (DENY_REASONS as readonly unknown[]).includes(value) ? (value as DenyReason) : null;
}

// Reads a code out of an object shaped `{ reason }` — the API's 403 `details`
// and the history state the portal seeds its own refusal screen with are the
// same shape. Returns null for anything not carrying a RECOGNISED code, so a
// caller can tell "no answer here" apart from "refused, for a reason I know".
//
// Requiring a recognised code rather than merely a truthy object is deliberate:
// unrelated router state (`{ from }`, say) must never read as a refusal.
export function readSeededReason(source: unknown): DenyReason | null {
  if (typeof source !== 'object' || source === null) return null;
  return asDenyReason((source as { reason?: unknown }).reason);
}

// The same read for a 403 already known to be a refusal, so there is always an
// answer. An unknown, missing or malformed code degrades to 'not_provisioned',
// whose wording ("you don't have access") is true of every refusal. That matters
// because a refused user must always be told something: a code the API adds
// after this bundle shipped would otherwise render a blank screen.
//
// 'not_provisioned' is also the safe default in the other direction — it never
// claims a group is missing (which would send an Okta administrator hunting)
// and never claims an outage (which would imply retrying helps).
export function readDenyReason(details: unknown): DenyReason {
  return readSeededReason(details) ?? 'not_provisioned';
}
